import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { safeNext } from "./LoginPage";
import { Btn, G_DARK, globalStyles } from "../components/Shared";

// Where the backend's Keycloak callback lands: /login/complete#token=<portal JWT>&next=<path>.
// The token travels in the fragment (never sent to a server); it is removed from
// the address bar first thing. Pattern from data-portal.
// If GET /auth/me then fails without a 401 (network error, 5xx), loginWithToken has kept the
// token: the page says so and Try again goes on to `next` (knowledge/tasks/completed/
// 2026-10-05-only-a-401-signs-the-user-out.md); only a 401 asks for a new sign-in.

const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function readFragment() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return { token: params.get("token"), next: params.get("next") };
}

export default function LoginCompletePage() {
  const { loginWithToken, retryAuth } = useAuth();
  const navigate = useNavigate();
  const [fragment] = useState(readFragment);
  const [failed, setFailed] = useState(false);
  const [unreachable, setUnreachable] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
    const { token, next } = fragment;
    if (!token || !JWT_SHAPE.test(token)) { setFailed(true); return; }
    loginWithToken(token)
      .then(() => navigate(safeNext(next), { replace: true }))
      .catch(e => { if (e.status === 401) setFailed(true); else setUnreachable(e); });
  }, [fragment, loginWithToken, navigate]);

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <style>{globalStyles}</style>
      <div style={{ maxWidth: 400, width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <h1 style={{ margin: "0 0 12px", fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>Unio</h1>
        {unreachable ? (
          <>
            <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 16 }}>
              Could not reach the server to finish the sign-in ({unreachable.message}). You are signed in; try again in a moment.
            </div>
            <Btn onClick={() => { retryAuth(); navigate(safeNext(fragment.next), { replace: true }); }}>Try again</Btn>
          </>
        ) : failed ? (
          <>
            <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 16 }}>
              The sign-in did not complete. Please sign in again.
            </div>
            <Link to="/login" style={{ fontSize: 14, color: G_DARK, fontWeight: 500 }}>Back to the sign-in page</Link>
          </>
        ) : (
          <p role="status" style={{ margin: 0, fontSize: 14, color: "#374151" }}>Signing you in…</p>
        )}
      </div>
    </div>
  );
}
